import { Extension } from "@tiptap/core";
import { formatStyle, parseStyle } from "./styleProps";

/**
 * Block indentation for paragraphs and headings.
 *
 * StarterKit has no equivalent: ProseMirror only understands list nesting, so
 * without this the `outdent`/`indent` buttons that the notice and factsheet
 * toolbars already carry would have nothing to do outside a list.
 *
 * Indentation is written as `margin-left`, not `padding-left` (TinyMCE 6's
 * default), because `margin-left` is in the notice's valid_styles allowlist
 * and in the properties bac/html_docx.py round-trips; padding-left would be
 * stripped on the way to the .docx.
 *
 * The value is read from and written back into the node's `style` attribute -
 * the one PreservedStyles owns - rather than a separate attribute of its own,
 * so a paragraph that already carried a margin has exactly one source of
 * truth. This extension therefore requires PreservedStyles to be loaded.
 *
 * Inside a list, indent/outdent defer to list nesting; see toolbarItems.js.
 */

// Enough to read what the stored documents actually use. An unrecognised unit
// is treated as no indent rather than guessed at.
const PX_PER_UNIT = { px: 1, pt: 96 / 72, in: 96, cm: 96 / 2.54, mm: 96 / 25.4 };

function toPx(value) {
  const match = /^(-?[\d.]+)\s*([a-z%]*)$/i.exec(String(value || "").trim());
  if (!match) return 0;
  const amount = parseFloat(match[1]);
  const unit = (match[2] || "px").toLowerCase();
  if (Number.isNaN(amount) || !PX_PER_UNIT[unit]) return 0;
  return amount * PX_PER_UNIT[unit];
}

export const Indent = Extension.create({
  name: "indent",

  addOptions() {
    return {
      types: ["paragraph", "heading"],
      // 40px is TinyMCE's own indent step, so documents keep the indent depth
      // they were authored with.
      step: 40,
      max: 400,
    };
  },

  addCommands() {
    const { types, step, max } = this.options;

    const shift = (delta) => () => ({ state, tr, dispatch }) => {
      const { from, to } = state.selection;
      let changed = false;

      state.doc.nodesBetween(from, to, (node, pos) => {
        if (!types.includes(node.type.name)) return;

        const declarations = parseStyle(node.attrs.style);
        const existing = declarations.find(([property]) => property === "margin-left");
        const current = existing ? toPx(existing[1]) : 0;
        const next = Math.min(Math.max(current + delta, 0), max);
        if (next === current) return;

        // The result is written in px whatever unit it came in as; the
        // rendered indent is unchanged, and px is what the docx converter
        // sees from TinyMCE-authored content too.
        const rest = declarations.filter(([property]) => property !== "margin-left");
        const style =
          formatStyle(next > 0 ? [...rest, ["margin-left", `${next}px`]] : rest) ||
          null;

        tr.setNodeMarkup(pos, undefined, { ...node.attrs, style });
        changed = true;
      });

      if (!changed) return false;
      if (dispatch) dispatch(tr);
      return true;
    };

    return {
      indent: shift(step),
      outdent: shift(-step),
    };
  },
});

export default Indent;
